import {
    ButtonAction,
    clipboardService,
    ExtAddButton,
    ExtEditButton,
    LayoutContentTitle,
    SearchButton
} from '@dv5/components'
import {
    DvCanvas,
    getCanvasContents,
    ItemAction,
    openSearchPalette,
    updateCanvasSettings,
    useAppDispatch,
    useAppSelector
} from '@dv5/domain'
import React, {FC, useState} from "react"
import {ExportMarkdownDialog} from "./components/ExportMarkdownDialog"
import {CanvasOptionsDialog} from "./components/CanvasOptionsDialog"
import {useCanvasSettings} from "./containers/hooks/useCanvasSettings"
import {canvasMoveService} from "./services/canvasMoveService"
import {canvasSettingsService} from "./services/canvasSettingsService"
import {alignWidgetsToGrid} from "./services/gridUtils"
import './CanvasHeader.less'

interface Props {
    canvas: DvCanvas
    onShowPopup: (isVisible: boolean) => void
    onSelectLayout: () => void
    onEdit?: () => void
    iconColor?: string
}

const actions: Array<ButtonAction> = [
    {
        text: 'Copy ID',
        action: ItemAction.COPY_ID,
    },
    {
        text: 'Settings',
        action: ItemAction.OPEN_OPTIONS,
        separatorBefore: true,
    },
    {
        text: 'Align to Grid',
        action: ItemAction.ALIGN_TO_GRID,
    },
    {
        text: 'Export to Markdown',
        action: ItemAction.EXPORT_MARKDOWN,
        separatorBefore: true,
    },
]

export const CanvasHeader: FC<Props> = ({
                                            canvas,
                                            onShowPopup,
                                            onSelectLayout,
                                            iconColor,
                                            onEdit,
                                        }) => {
    const [showOptionsDialog, setShowOptionsDialog] = useState(false)
    const [showExportMarkdown, setShowExportMarkdown] = useState(false)
    const dispatch = useAppDispatch()
    const canvasContents = useAppSelector(getCanvasContents)
    const settings = useCanvasSettings(canvas.id)

    const toggleAlignToGrid = () => {
        const next = !settings.alignToGrid
        dispatch(updateCanvasSettings({canvasId: canvas.id, changes: {alignToGrid: next}}))
        canvasSettingsService.updateSettings(canvas.id, {alignToGrid: next})
    }

    const handleAction = (action: ItemAction) => {
        if (action === ItemAction.EDIT) {
            if (!settings.readOnly) onEdit?.()
        } else if (action === ItemAction.COPY_ID) {
            clipboardService.copyTo(canvas.id, false)
        } else if (action === ItemAction.OPEN_OPTIONS) {
            openOptions()
        } else if (action === ItemAction.ALIGN_TO_GRID) {
            if (settings.readOnly) return
            const widgets = canvasContents[canvas.id]?.widgets || []
            if (widgets.length > 0) {
                canvasMoveService.moveWidgets(canvas.id, alignWidgetsToGrid(widgets))
            }
        } else if (action === ItemAction.EXPORT_MARKDOWN) {
            openExportMarkdown()
        }
    }

    const openSearch = () => {
        dispatch(openSearchPalette({scope: 'canvas'}))
    }

    const openOptions = () => {
        setShowOptionsDialog(true)
        onShowPopup(true)
    }

    const closeOptions = () => {
        setShowOptionsDialog(false)
        onShowPopup(false)
    }

    const openExportMarkdown = () => {
        setShowExportMarkdown(true)
        onShowPopup(true)
    }

    const closeExportMarkdown = () => {
        setShowExportMarkdown(false)
        onShowPopup(false)
    }

    const toggleDisplayGrid = (next: boolean) => {
        dispatch(updateCanvasSettings({canvasId: canvas.id, changes: {displayGrid: next}}))
        canvasSettingsService.updateSettings(canvas.id, {displayGrid: next})
    }

    const toggleAlignToGridDialog = (next: boolean) => {
        dispatch(updateCanvasSettings({canvasId: canvas.id, changes: {alignToGrid: next}}))
        canvasSettingsService.updateSettings(canvas.id, {alignToGrid: next})
    }

    const toggleMarkdownComments = (next: boolean) => {
        dispatch(updateCanvasSettings({canvasId: canvas.id, changes: {markdownComments: next}}))
        canvasSettingsService.updateSettings(canvas.id, {markdownComments: next})
    }

    return (
        <>
            <SearchButton className="canvas-header-search-button"
                          iconColor={iconColor}
                          onSearch={openSearch}
                          tooltipText="Search Canvas Content"/>
            <LayoutContentTitle title={canvas.title} onSelect={onSelectLayout}/>
            {settings.readOnly && <span role="status">Read only</span>}
            <ExtEditButton
                actions={[...(!settings.readOnly && onEdit ? [{text: 'Edit', action: ItemAction.EDIT}] : []),
                    ...actions.filter(action => !settings.readOnly || action.action !== ItemAction.ALIGN_TO_GRID)]}
                onEdit={handleAction}
                tooltipText="Canvas actions"
                onShowPopup={onShowPopup}
                iconColor={iconColor}
            />
            {showOptionsDialog && (
                <CanvasOptionsDialog
                    isOpen={showOptionsDialog}
                    onClose={closeOptions}
                    canvasId={canvas.id}
                    settings={settings}
                    onToggleReadOnly={readOnly => {
                        dispatch(updateCanvasSettings({canvasId: canvas.id, changes: {readOnly}}))
                        canvasSettingsService.updateSettings(canvas.id, {readOnly})
                    }}
                    onToggleDisplayGrid={toggleDisplayGrid}
                    onToggleAlignToGrid={toggleAlignToGridDialog}
                    onToggleMarkdownComments={toggleMarkdownComments}
                />
            )}
            {showExportMarkdown && (
                <ExportMarkdownDialog
                    canvas={canvas}
                    onClose={closeExportMarkdown}
                />
            )}
        </>
    )
}
